export interface TenantColorRamp {
  readonly 50: string;
  readonly 100: string;
  readonly 500: string;
  readonly 600: string;
  readonly 700: string;
  readonly 900: string;
}

type Rgb = readonly [number, number, number];

function hexToRgb(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (match === null) return null;
  const value = match[1]!;
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

function toHexChannel(channel: number): string {
  return Math.round(Math.min(255, Math.max(0, channel)))
    .toString(16)
    .padStart(2, "0");
}

function toHex([r, g, b]: Rgb): string {
  return `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;
}

function mixWithWhite([r, g, b]: Rgb, fraction: number): string {
  return toHex([
    r + (255 - r) * fraction,
    g + (255 - g) * fraction,
    b + (255 - b) * fraction,
  ]);
}

function scaleTowardBlack([r, g, b]: Rgb, factor: number): string {
  return toHex([r * factor, g * factor, b * factor]);
}

/**
 * Reproduces the default palette's own tonal ramp: globals.css's
 * blue-purple-{50,100,600,700,900} are each exactly the 500 base mixed
 * toward white (50/100) or scaled toward black (600/700/900) by a fixed
 * fraction (0.9/0.8 mix, 0.8/0.6/0.2 scale). Applying the same formula to a
 * tenant's primaryColor keeps every derived shade in the same relationship
 * the hand-picked default shades have to each other.
 */
export function generateTenantColorRamp(
  primaryColor: string,
): TenantColorRamp | null {
  const rgb = hexToRgb(primaryColor);
  if (rgb === null) return null;
  return {
    50: mixWithWhite(rgb, 0.9),
    100: mixWithWhite(rgb, 0.8),
    500: toHex(rgb),
    600: scaleTowardBlack(rgb, 0.8),
    700: scaleTowardBlack(rgb, 0.6),
    900: scaleTowardBlack(rgb, 0.2),
  };
}
